import { Link } from 'react-router-dom';
import { Body1, Body1Strong, Caption1, Card, Subtitle2 } from '@fluentui/react-components';
import { ArrowRightRegular } from '@fluentui/react-icons';
import { Avatar, useUsers } from '@modules/users';

import { FindingStatusBadge } from '../shared';
import { getFindingPath } from '../../helpers';
import { TReview } from '../../types';

const formatUpdateTime = (timestamp: number) =>
  new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const ReviewLatestUpdates = ({ review, productId }: { review: TReview; productId?: string }) => {
  const { users } = useUsers();
  const findAuthor = (id: string | undefined, name: string) =>
    users?.find((user) => (id ? user._id === id : user.name === name || user.mail === name));
  const starter = findAuthor(review.startedById, review.startedBy);
  const updates = [...(review.statusUpdates ?? [])].sort((a, b) => b.createdAt - a.createdAt);

  return (
    <Card size="large" className="min-w-0 min-h-[310px]">
      <Subtitle2>Latest updates</Subtitle2>
      <ul aria-label="Review updates" className="flex flex-col gap-4 m-0 p-0 list-none max-h-[240px] overflow-y-auto">
        {updates.map((update) => {
          const finding = review.findings.find((entry) => entry.id === update.findingId);
          const author = findAuthor(update.authorId, update.author);
          return (
            <li key={update.id} className="flex items-start gap-3">
              <Avatar user={author} name={update.author} size={28} tooltip={author ? 'name' : 'none'} />
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <Body1Strong>{author?.name || update.author}</Body1Strong>
                <Link
                  to={getFindingPath(review.id, update.findingId, productId)}
                  className="text-neutralFg-1 hover:underline break-words"
                >
                  <Body1>{finding?.title ?? 'Finding'}</Body1>
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                  <FindingStatusBadge status={update.from} />
                  <ArrowRightRegular aria-label="changed to" />
                  <FindingStatusBadge status={update.to} />
                </div>
                <Caption1 className="text-neutralFg-3">
                  <time dateTime={new Date(update.createdAt).toISOString()}>{formatUpdateTime(update.createdAt)}</time>
                </Caption1>
              </div>
            </li>
          );
        })}
        <li className="flex items-start gap-3">
          <Avatar user={starter} name={review.startedBy} size={28} tooltip={starter ? 'name' : 'none'} />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <Body1>
              Review started{review.startedBy || starter ? ' by ' : ''}
              <Body1Strong>{starter?.name || review.startedBy}</Body1Strong>
            </Body1>
            <Caption1 className="text-neutralFg-3">
              <time dateTime={new Date(review.startedAt).toISOString()}>{formatUpdateTime(review.startedAt)}</time>
            </Caption1>
          </div>
        </li>
      </ul>
    </Card>
  );
};
